import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSystemMetrics } from '@/hooks/useMetrics';
import { listIndexes, type OpenSearchIndexInfo } from '@/services/reindex-api';
import { getSweeperMetrics } from '@/services/dashboard-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Database } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Activity,
  FileText,
  CheckCircle2,
  TrendingUp,
  Zap,
  HeartPulse,
  Users,
  Loader2,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import type { EntityType } from '../types';

export default function OverviewPanel() {
  const [entityFilter, setEntityFilter] = useState<EntityType | undefined>();
  const { metrics, isLoading } = useSystemMetrics(entityFilter);

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' => {
    switch (status) {
      case 'healthy':
        return 'default';
      case 'degraded':
        return 'secondary';
      case 'error':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  // Prepare entity breakdown data for pie chart (dynamic - supports person, company, opportunity, etc.)
  const ENTITY_LABELS: Record<string, string> = {
    person: 'Persons',
    company: 'Companies',
    opportunity: 'Opportunities',
    note: 'Notes',
    list: 'Lists',
    listEntry: 'List Entries',
  };
  const ENTITY_COLORS: Record<string, string> = {
    person: '#3b82f6', // blue
    company: '#8b5cf6', // purple
    opportunity: '#10b981', // emerald
    note: '#f59e0b', // amber
    list: '#06b6d4', // cyan
    listEntry: '#ec4899', // pink
  };
  const entityBreakdownData = metrics.entityBreakdown
    ? Object.entries(metrics.entityBreakdown)
        .filter(([, value]) => value > 0)
        .map(([key, value]) => ({
          name: ENTITY_LABELS[key] || key,
          value,
          entityType: key,
        }))
    : [];

  // Get timezone info for "Today" definition
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: userTimezone,
  });

  // Cards grouped by what question they answer for the operator:
  //   pipeline → "What's moving right now?"
  //   throughput → "How fast?"
  //   health    → "Anything wrong?"
  //   index     → "What's in the index?" (lifetime / cumulative)
  type MetricCard = {
    title: string;
    value: string;
    icon: typeof Activity;
    description: string;
    location: string;
    iconColor: string;
    suffix?: string;
  };
  const pipelineCards: MetricCard[] = [
    {
      title: 'Pending Documents',
      value: metrics.pendingDocuments.toLocaleString(),
      icon: FileText,
      description: 'Queued, compiled, or batched — waiting to reach OpenSearch',
      location: 'Postgres Document + IngestQueueItem + IndexQueueItem',
      iconColor: 'text-purple-500',
    },
    {
      title: 'Batched (Pending Send)',
      value: (metrics.batchedNotYetAtAnthropic || 0).toLocaleString(),
      icon: Clock,
      description: 'Claimed by batch-creator, not yet sent to Anthropic (5-15s)',
      location: 'Postgres Document.status=batched, no BatchJob row yet',
      iconColor: (metrics.batchedNotYetAtAnthropic || 0) > 100 ? 'text-orange-500' : 'text-gray-400',
    },
    {
      title: 'AI Processing (At Anthropic)',
      value: (metrics.batchedAtAnthropic || 0).toLocaleString(),
      icon: Activity,
      description: 'Documents in active Anthropic batch jobs',
      location: `${metrics.activeBatches} active batches at Anthropic Batch API`,
      iconColor: 'text-indigo-500',
    },
    {
      title: 'Anthropic Batch Jobs',
      value: metrics.activeBatches.toLocaleString(),
      icon: Activity,
      description: 'Active batch jobs at Anthropic',
      location: 'Anthropic Batch API',
      iconColor: 'text-blue-500',
    },
  ];
  const throughputCards: MetricCard[] = [
    {
      title: 'Indexed Last 24h',
      value: metrics.indexedToday.toLocaleString(),
      icon: TrendingUp,
      description: `${metrics.currentThroughput.toLocaleString()}/hr current rate`,
      location: 'Postgres Document with indexedAt in last 24h',
      iconColor: 'text-green-500',
    },
    {
      title: 'Index Throughput',
      value: metrics.currentThroughput.toLocaleString(),
      icon: Zap,
      description: 'Documents indexed in the last hour (rolling 60m window)',
      location: 'Postgres Document with status=indexed in last hour',
      iconColor: 'text-yellow-500',
      suffix: '/hr',
    },
    {
      title: 'Success Rate',
      value: `${metrics.successRate.toFixed(1)}%`,
      icon: TrendingUp,
      description: 'Indexed vs failed today',
      location: 'Calculated from indexed and failed document counts',
      iconColor: metrics.successRate >= 90 ? 'text-emerald-500' : 'text-orange-500',
    },
  ];
  const healthCards: MetricCard[] = [
    {
      title: 'Failed Documents',
      value: (metrics.failedDocuments || 0).toLocaleString(),
      icon: AlertCircle,
      description: 'index_error or ai_error — requires reprocessing',
      location: 'Postgres Document.status in (index_error, ai_error)',
      iconColor: (metrics.failedDocuments || 0) > 0 ? 'text-red-500' : 'text-gray-400',
    },
    {
      title: 'DLQ (Retry Queue)',
      value: (metrics.dlqDocuments || 0).toLocaleString(),
      icon: AlertCircle,
      description: 'Failed after max retries — manual review needed',
      location: `Entity Ingest DLQ: ${metrics.queueDepths?.entityIngestDLQ || 0}, OpenSearch Index DLQ: ${metrics.queueDepths?.opensearchIndexDLQ || 0}. Auto-retry: up to 3 times, every 10 minutes.`,
      iconColor: (metrics.dlqDocuments || 0) > 0 ? 'text-orange-500' : 'text-gray-400',
    },
  ];
  const indexCards: MetricCard[] = [
    {
      title: 'Total Indexed',
      value: metrics.totalIndexed.toLocaleString(),
      icon: CheckCircle2,
      description: 'Lifetime documents in OpenSearch (all entity types)',
      location: 'OpenSearch (via index aliases) - direct count query',
      iconColor: 'text-emerald-500',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header with Today Definition and Entity Filter */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold">
            System Overview
            {isLoading && (
              <Loader2 className="inline-block ml-2 h-5 w-5 animate-spin text-muted-foreground" />
            )}
          </h2>
          <p className="text-sm text-muted-foreground">
            Today is {currentDate} • Timezone: {userTimezone}
            {isLoading && <span className="ml-2 text-blue-500">Loading...</span>}
          </p>
        </div>

        {/* Entity Type Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Entity Type</label>
          <Select
            value={entityFilter || 'all'}
            onValueChange={(value) =>
              setEntityFilter(value === 'all' ? undefined : (value as 'person' | 'company' | 'opportunity' | 'note' | 'list' | 'listEntry'))
            }
            disabled={isLoading}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All entities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="person">Person</SelectItem>
              <SelectItem value="company">Company</SelectItem>
              <SelectItem value="opportunity">Opportunity</SelectItem>
              <SelectItem value="note">Note</SelectItem>
              <SelectItem value="list">List</SelectItem>
              <SelectItem value="listEntry">List Entry</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sectioned metric cards — each section answers one operator question.
          Pipeline state, throughput, and health are the things you check on
          a live system; the lifetime totals + entity breakdown live below
          because they're context, not signals. */}
      <TooltipProvider delayDuration={200}>
        <MetricSection
          title="Live pipeline state"
          subtitle="What's moving through the pipeline right now"
          cards={pipelineCards}
        />
        <MetricSection
          title="Throughput"
          subtitle="How fast documents are getting indexed"
          cards={throughputCards}
        />
        <MetricSection
          title="Health"
          subtitle="Things that need attention"
          extraCards={
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">System Status</CardTitle>
                <HeartPulse className="h-4 w-4 text-pink-500" />
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={getStatusVariant(metrics.systemStatus)}
                    className="text-lg px-3 py-1"
                  >
                    {metrics.systemStatus}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {metrics.systemStatus === 'healthy' && 'All systems operational'}
                  {metrics.systemStatus === 'degraded' && 'Some issues detected'}
                  {metrics.systemStatus === 'error' && 'Critical issues present'}
                </p>
              </CardContent>
            </Card>
          }
          cards={healthCards}
        />
        <MetricSection
          title="Index contents"
          subtitle="What's already in OpenSearch — context only, not a pipeline signal"
          cards={indexCards}
          extraCards={
            metrics.entityBreakdown ? (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Entity Breakdown</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={entityBreakdownData}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={({ name, percent, value }) =>
                          `${name}: ${value.toLocaleString()} (${(percent * 100).toFixed(0)}%)`
                        }
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {entityBreakdownData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={ENTITY_COLORS[entry.entityType] || `hsl(${index * 120}, 70%, 50%)`}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            ) : undefined
          }
        />
        <OpenSearchSystemSection />
        <VectorSweeperSection />
      </TooltipProvider>
    </div>
  );
}

// ─── Vector quality sweeper ──────────────────────────────────────────────
//
// Surfaces the daily self-kNN audit: how recently it ran, whether it's
// finding broken (`1`-fallback) vectors, and the per-entity sweep state.
// Most of the time "Broken (30d)" should read 0 — a nonzero value means the
// ML model was degrading writes and the sweeper re-enqueued docs to fix them.

function fmtAgo(ms: number | null): string {
  if (!ms) return '—';
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function VectorSweeperSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['vector-sweeper-metrics'],
    queryFn: () => getSweeperMetrics(20),
    refetchInterval: 60_000,
  });
  const runs = data?.runs ?? [];
  const summary = data?.summary;
  const cursors = data?.cursors ?? [];

  return (
    <div className="space-y-2 pt-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Vector quality sweeper
        </h3>
        <p className="text-xs text-muted-foreground/70">
          Daily self-kNN audit of recently-written vectors. Re-enqueues any broken
          (<code>1</code>-fallback) embeddings for a rewrite. Broken should normally be 0.
        </p>
      </div>
      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : runs.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            No sweep runs recorded yet — the first run will appear here after the next cron tick.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Last sweep</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{fmtAgo(runs[0].ranAt)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Broken (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${
                    summary && summary.brokenLast30d > 0 ? 'text-red-600' : 'text-green-600'
                  }`}
                >
                  {summary?.brokenLast30d ?? 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.lastBrokenAt ? `last ${fmtAgo(summary.lastBrokenAt)}` : 'none found'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Inspected (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums">
                  {(summary?.inspectedLast30d ?? 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Runs (30d)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums">{summary?.runsLast30d ?? 0}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="pt-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-2 font-medium">Run</th>
                    <th className="pb-2 font-medium text-right">Inspected</th>
                    <th className="pb-2 font-medium text-right">Broken</th>
                    <th className="pb-2 font-medium text-right">Re-enqueued</th>
                    <th className="pb-2 font-medium text-right">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-t border-muted/20">
                      <td className="py-1.5 text-xs">{new Date(r.ranAt).toLocaleString()}</td>
                      <td className="py-1.5 text-right tabular-nums">
                        {r.totalInspected.toLocaleString()}
                      </td>
                      <td
                        className={`py-1.5 text-right tabular-nums ${
                          r.totalBroken > 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'
                        }`}
                      >
                        {r.totalBroken}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                        {r.totalEnqueued}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                        {(r.durationMs / 1000).toFixed(0)}s
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {cursors.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground mb-2">Per-entity sweep state</p>
                <div className="flex flex-wrap gap-2">
                  {cursors.map((c) => (
                    <Badge key={`${c.alias}:${c.vectorField}`} variant="secondary" className="text-xs">
                      {c.alias}: swept {fmtAgo(c.lastSweepAt)}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ─── OpenSearch system overview ──────────────────────────────────────────
//
// Shows every non-system index on the cluster with its doc count, store
// size, health, and which aliases point to it. The aliased indices get a
// pill so the operator can scan at a glance which version each entity
// type is currently serving from. Refreshes every 10s — fast enough to
// watch a bulk reindex grow without slamming OS with cat queries.

function OpenSearchSystemSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['os-indices-all'],
    queryFn: () => listIndexes(),
    refetchInterval: 10_000,
  });
  const indices = data?.indices ?? [];

  // Group indices by their alias-prefix root so v10/v11/v12 of the same
  // entity sit together. e.g. "people_v10", "people_v11" → group "people".
  const groups = new Map<string, OpenSearchIndexInfo[]>();
  for (const idx of indices) {
    const root = idx.name.replace(/_v\d+.*$/, '') || idx.name;
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(idx);
  }
  const sortedRoots = [...groups.keys()].sort();

  return (
    <div className="space-y-2 pt-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          OpenSearch system
        </h3>
        <p className="text-xs text-muted-foreground/70">
          All cluster indices. Aliased indices are the ones serving live queries — refreshes every 10s.
        </p>
      </div>
      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : indices.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">No indices visible.</CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="pb-2 font-medium">Index</th>
                  <th className="pb-2 font-medium text-right">Docs</th>
                  <th className="pb-2 font-medium text-right">Size</th>
                  <th className="pb-2 font-medium">Health</th>
                  <th className="pb-2 font-medium">Aliases</th>
                </tr>
              </thead>
              <tbody>
                {sortedRoots.flatMap((root) => {
                  const groupRows = groups.get(root)!.sort((a, b) => a.name.localeCompare(b.name));
                  return groupRows.map((idx, i) => (
                    <tr
                      key={idx.name}
                      className={
                        i === 0 ? 'border-t border-muted/40' : 'border-t border-muted/20'
                      }
                    >
                      <td className="py-1.5 font-mono text-xs">
                        <span className="inline-flex items-center gap-1.5">
                          <Database className="h-3 w-3 text-muted-foreground" />
                          {idx.name}
                        </span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {idx.docsCount.toLocaleString()}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                        {idx.storeSize}
                      </td>
                      <td className="py-1.5">
                        <span
                          className={
                            idx.health === 'green'
                              ? 'text-green-600'
                              : idx.health === 'yellow'
                                ? 'text-amber-600'
                                : 'text-red-600'
                          }
                        >
                          ●
                        </span>{' '}
                        <span className="text-xs">{idx.health}</span>
                      </td>
                      <td className="py-1.5">
                        {idx.aliases.length === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {idx.aliases.map((alias) => (
                              <Badge
                                key={alias}
                                variant={idx.isAliasTarget ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {alias}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Section renderer ──────────────────────────────────────────────────
//
// Each section has a header strip (label + one-line subtitle) so the
// operator can scan the page top-to-bottom and skip sections that aren't
// relevant to the current question.

interface MetricCardData {
  title: string;
  value: string;
  icon: typeof Activity;
  description: string;
  location: string;
  iconColor: string;
  suffix?: string;
}

function MetricSection({
  title,
  subtitle,
  cards,
  extraCards,
}: {
  title: string;
  subtitle?: string;
  cards: MetricCardData[];
  extraCards?: React.ReactNode;
}) {
  return (
    <div className="space-y-2 pt-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        {subtitle && (
          <p className="text-xs text-muted-foreground/70">{subtitle}</p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((metric) => {
          const Icon = metric.icon;
          return (
            <Tooltip key={metric.title}>
              <TooltipTrigger asChild>
                <Card className="cursor-help hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
                    <Icon className={`h-4 w-4 ${metric.iconColor}`} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {metric.value}
                      {metric.suffix && (
                        <span className="text-sm text-muted-foreground ml-1">{metric.suffix}</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{metric.description}</p>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="tooltip-content max-w-xs p-3">
                <div className="space-y-2">
                  <p className="font-semibold text-sm">{metric.title}</p>
                  <p className="text-xs">{metric.description}</p>
                  <div className="pt-2 border-t">
                    <p className="text-xs font-medium text-muted-foreground">Location:</p>
                    <p className="text-xs">{metric.location}</p>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
        {extraCards}
      </div>
    </div>
  );
}
